import React, { useState } from 'react';
import classNames from 'classnames';
import { sendEvent } from '@JohnLewisPartnership/experiments-utils';
import styles from './DesktopMenuTabs.scss';
import { formatTestId } from '../../../utils/test-id/format-testid';

type DesktopMenuTabsProps = {
  tabsToDisplay?: readonly string[];
  activeTab?: string;
  selectedTab?: string;
  isMenuOpen?: boolean;
  onTabFocus?: (tab: string) => void;
  onActiveTabChange: (tab: string) => void;
  onActiveTabKeyDown?: (
    event: React.KeyboardEvent<HTMLButtonElement>,
    tab: string
  ) => void;
  isTouch?: boolean;
};

export const DesktopMenuTabs = ({
  tabsToDisplay = [],
  activeTab,
  selectedTab,
  isMenuOpen = false,
  onTabFocus,
  onActiveTabChange,
  onActiveTabKeyDown,
  isTouch = false,
}: DesktopMenuTabsProps) => {
  const [internalActiveTab, setInternalActiveTab] = useState('');
  const currentTab = (activeTab ?? internalActiveTab) || tabsToDisplay[0] || '';
  const highlightedTab = selectedTab ?? currentTab;

  const handleTabClick = (tab: string) => {
    if (activeTab === undefined) {
      setInternalActiveTab(tab);
    }
    onActiveTabChange(tab);
    sendEvent(`navigation.tab.${tab.replace(/\s+/g, '-').toLowerCase()}.click`);
  };

  return (
    <ul className={styles.desktopMenuTabs} data-testid="navigation-menu-tabs">
      {tabsToDisplay.map(tab => (
        <li
          key={tab}
          className={classNames(styles.desktopMenuTab)}
          {...(!isTouch && {
            onMouseEnter: () => handleTabClick(tab),
          })}
        >
          <button
            className={classNames(styles.desktopMenuTabButton, {
              [styles.desktopMenuTabButton__active]: tab === highlightedTab,
            })}
            {...(isTouch && { onClick: () => handleTabClick(tab) })}
            onFocus={() => onTabFocus?.(tab)}
            onKeyDown={event => onActiveTabKeyDown?.(event, tab)}
            tabIndex={isMenuOpen ? (tab === currentTab ? 0 : -1) : 0}
            data-current-tab={tab === currentTab ? 'true' : 'false'}
            data-testid={formatTestId(tab, 'nav-menu-tab-')}
          >
            {tab}
          </button>
        </li>
      ))}
    </ul>
  );
};
